import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Alert,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  BugReport as TestIcon,
  CheckCircle as CompleteIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTDD } from '../../context/TDDContext';
import TDDService from '../../services/tddService';
import TDDWorkspaceView from './TDDWorkspaceView';
import TDDTaskManager from './TDDTaskManager';
import TDDProgressTracker from './TDDProgressTracker';

/**
 * Main TDD Workflow Manager Component
 * Orchestrates the entire TDD workflow process
 */
const TDDWorkflowManager = ({ projectId }) => {
  const {
    tddState,
    updateWorkspace,
    updateProgress,
    resetTDDState
  } = useTDD();

  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState(null);
  const [isWorkspaceReady, setIsWorkspaceReady] = useState(false);

  /**
   * Initialize TDD workflow
   */
  const initializeTDDWorkflow = async () => {
    setIsInitializing(true);
    setError(null);
    
    try {
      updateProgress({
        phase: 'initialization',
        message: 'Initializing TDD workspace...',
        percentage: 10
      });

      // Step 1: Initialize workspace
      const initResponse = await TDDService.initializeWorkspace(projectId);
      
      if (initResponse.success) {
        updateWorkspace({
          status: 'initializing',
          path: initResponse.workspace_path
        });

        updateProgress({
          message: 'Creating workspace structure...',
          percentage: 30
        });

        // Step 2: Create workspace
        const createResponse = await TDDService.createWorkspace(projectId, {
          extract_md_files: true,
          create_tdd_files: true
        });

        if (createResponse.success) {
          updateWorkspace({
            status: 'ready',
            files: createResponse.files || [],
            lastUpdated: new Date().toISOString()
          });

          updateProgress({
            phase: 'analysis',
            message: 'Workspace ready. Analyzing user stories...',
            percentage: 60
          });

          // Step 3: Analyze user stories
          const analysisResponse = await TDDService.analyzeUserStories(
            projectId,
            createResponse.workspace_path
          );

          if (analysisResponse.success) {
            updateProgress({
              phase: 'ready',
              message: 'TDD workspace initialized successfully',
              percentage: 100
            });
            setIsWorkspaceReady(true);
          }
        }
      }
    } catch (err) {
      console.error('TDD initialization error:', err);
      setError(err.message || 'Failed to initialize TDD workflow');
      updateWorkspace({ status: 'error' });
      updateProgress({
        phase: 'error',
        message: 'Initialization failed',
        percentage: 0
      });
    } finally {
      setIsInitializing(false);
    }
  };

  /**
   * Reset workflow
   */
  const handleResetWorkflow = () => {
    resetTDDState();
    setIsWorkspaceReady(false);
    setError(null);
  };

  /**
   * Check workspace status on component mount
   */
  useEffect(() => {
    const checkWorkspaceStatus = async () => {
      try {
        const statusResponse = await TDDService.getWorkspaceStatus(projectId);
        if (statusResponse.success && statusResponse.workspace_exists) {
          updateWorkspace({
            status: 'ready',
            path: statusResponse.workspace_path,
            files: statusResponse.files || []
          });
          setIsWorkspaceReady(true);
          updateProgress({
            phase: 'ready',
            message: 'Workspace ready',
            percentage: 100
          });
        }
      } catch (err) {
        console.error('Error checking workspace status:', err);
      }
    };

    checkWorkspaceStatus();
  }, [projectId, updateWorkspace, updateProgress]);

  /**
   * Get status color for workspace
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'ready': return 'success';
      case 'initializing': return 'info';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  /**
   * Get phase icon
   */
  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'initialization': return <InfoIcon />;
      case 'analysis': return <CodeIcon />;
      case 'development': return <PlayIcon />;
      case 'testing': return <TestIcon />;
      case 'ready': return <CompleteIcon />;
      case 'error': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          TDD Development Workflow
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Test-Driven Development with Ralphio Integration
        </Typography>
      </Box>

      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getPhaseIcon(tddState.progress.phase)}
              <Typography variant="h6">
                Workspace Status
              </Typography>
              <Chip 
                label={tddState.workspace.status.replace('_', ' ').toUpperCase()} 
                color={getStatusColor(tddState.workspace.status)}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh Status">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {isWorkspaceReady && (
                <Tooltip title="Reset Workflow">
                  <IconButton onClick={handleResetWorkflow}>
                    <StopIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Progress Bar */}
          {tddState.progress.percentage > 0 && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={tddState.progress.percentage} 
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {tddState.progress.message}
              </Typography>
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Workspace Path */}
          {tddState.workspace.path && (
            <Typography variant="body2" color="text.secondary">
              Workspace: {tddState.workspace.path}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      {!isWorkspaceReady && !isInitializing && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Initialize TDD Workspace
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Set up your Test-Driven Development environment with Ralphio integration.
              This will extract project documentation, create TDD templates, and prepare your workspace.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayIcon />}
              onClick={initializeTDDWorkflow}
              disabled={isInitializing}
            >
              Initialize TDD Workspace
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Workspace Ready - Show Components */}
      {isWorkspaceReady && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TDDProgressTracker projectId={projectId} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TDDTaskManager projectId={projectId} />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TDDWorkspaceView projectId={projectId} />
          </Grid>
        </Grid>
      )}

      {/* Loading State */}
      {isInitializing && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Initializing TDD Workspace...
            </Typography>
            <LinearProgress sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              This may take a few minutes. Please wait...
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default TDDWorkflowManager;